import React, { useEffect, useState } from 'react'
import {
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    FormGroup,
    Radio,
    RadioGroup,
    TextField,
    Typography,
} from '@material-ui/core'

import { LeadsExitEdgeFields } from './LeadsExitEdgeFields'
import { ProspectsToLeadsEdgeFields } from './ProspectsToLeadsEdgeFields'
import {
    defaultConversionRules,
    enabledTriggersFromRules,
    rulesHavePmApproval,
} from './conversionRulesUtils'
import {
    PipelineStepInstance,
    PipelineTransition,
    TransitionExitGate,
} from './pipelineBuilder.types'
import { getCatalogEntry } from './stepCatalog'
import { ConversionTriggerRule } from './transitionConfig.types'
import {
    isLeadsExitFromStep,
    isProspectsToLeadsTransition,
} from './transitionConfigUtils'
import { getTransitionTriggerOptions } from './transitionTriggerCatalog'
import { summarizeTransition } from './transitionUtils'

interface EdgeEditDialogProps {
    open: boolean
    fromStep: PipelineStepInstance | null
    toStep: PipelineStepInstance | null
    transition: PipelineTransition | null
    readOnly?: boolean
    onClose: () => void
    onSave: (
        fromStepId: string,
        toStepId: string,
        transition: PipelineTransition,
    ) => void
}

export const EdgeEditDialog: React.FC<EdgeEditDialogProps> = ({
    open,
    fromStep,
    toStep,
    transition,
    readOnly = false,
    onClose,
    onSave,
}) => {
    const [exitGate, setExitGate] = useState<TransitionExitGate>('none')
    const [reviewPrompt, setReviewPrompt] = useState('')
    const [triggers, setTriggers] = useState<string[]>([])
    const [allowPmOverride, setAllowPmOverride] = useState(true)
    const [conversionRules, setConversionRules] = useState<
        ConversionTriggerRule[]
    >(() => defaultConversionRules())

    useEffect(() => {
        if (transition) {
            setExitGate(transition.exitGate)
            setReviewPrompt(transition.reviewPrompt ?? '')
            setTriggers(transition.triggers)
            setAllowPmOverride(transition.allowPmOverride ?? true)
            if (transition.conversionRules) {
                setConversionRules(transition.conversionRules)
            }
        }
    }, [transition])

    if (!fromStep || !toStep || !transition) {
        return null
    }

    const fromLabel = getCatalogEntry(fromStep.type).label
    const toLabel = getCatalogEntry(toStep.type).label
    const isConversionEdge = isProspectsToLeadsTransition(fromStep, toStep)
    const isLeadsExitEdge = isLeadsExitFromStep(fromStep)
    const triggerOptions = getTransitionTriggerOptions(fromStep.type)

    const previewTransition: PipelineTransition = isConversionEdge
        ? {
              ...transition,
              exitMode: 'conversion',
              triggers: enabledTriggersFromRules(conversionRules),
              conversionRules,
              exitGate: rulesHavePmApproval(conversionRules)
                  ? 'pm_review'
                  : 'none',
              reviewPrompt: reviewPrompt || undefined,
          }
        : isLeadsExitEdge
        ? {
              ...transition,
              exitMode: 'all',
              triggers,
              allowPmOverride,
              exitGate: 'none',
              reviewPrompt: undefined,
          }
        : {
              ...transition,
              exitMode: 'any',
              exitGate,
              reviewPrompt: reviewPrompt || undefined,
              triggers,
          }

    const toggleTrigger = (trigger: string, checked: boolean) => {
        setTriggers((prev) => {
            const next = new Set(prev)
            if (checked) {
                next.add(trigger)
            } else {
                next.delete(trigger)
            }
            return Array.from(next)
        })
    }

    const handleSave = () => {
        onSave(fromStep.id, toStep.id, previewTransition)
        onClose()
    }

    const enabledCount = isConversionEdge
        ? enabledTriggersFromRules(conversionRules).length
        : triggers.length

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                {fromLabel} → {toLabel}
            </DialogTitle>
            <DialogContent dividers>
                <Typography variant="body2" color="textSecondary" paragraph>
                    {isConversionEdge
                        ? 'What converts a prospect to a lead'
                        : isLeadsExitEdge
                        ? 'What must be complete before someone leaves Leads?'
                        : `Define what must happen to move someone from ${fromLabel} into ${toLabel}.`}
                </Typography>

                <Typography
                    variant="subtitle2"
                    style={{ marginBottom: 16, fontWeight: 600 }}
                >
                    {summarizeTransition(fromStep, toStep, previewTransition)}
                </Typography>

                {isConversionEdge ? (
                    <ProspectsToLeadsEdgeFields
                        rules={conversionRules}
                        reviewPrompt={reviewPrompt}
                        readOnly={readOnly}
                        onRulesChange={setConversionRules}
                        onReviewPromptChange={setReviewPrompt}
                    />
                ) : isLeadsExitEdge ? (
                    <LeadsExitEdgeFields
                        triggers={triggers}
                        allowPmOverride={allowPmOverride}
                        readOnly={readOnly}
                        onTriggersChange={setTriggers}
                        onAllowPmOverrideChange={setAllowPmOverride}
                    />
                ) : (
                    <>
                        <Typography variant="subtitle2" gutterBottom>
                            Move to {toLabel} when
                        </Typography>
                        <Typography
                            variant="body2"
                            color="textSecondary"
                            paragraph
                        >
                            A person advances when any selected condition is
                            met.
                        </Typography>
                        <FormGroup style={{ marginBottom: 16 }}>
                            {triggerOptions.map((option) => (
                                <FormControlLabel
                                    key={option.value}
                                    control={
                                        <Checkbox
                                            checked={triggers.includes(
                                                option.value,
                                            )}
                                            onChange={(_, checked) =>
                                                toggleTrigger(
                                                    option.value,
                                                    checked,
                                                )
                                            }
                                            disabled={readOnly}
                                            color="primary"
                                        />
                                    }
                                    label={option.label}
                                />
                            ))}
                        </FormGroup>

                        <Typography variant="subtitle2" gutterBottom>
                            Approval
                        </Typography>
                        <RadioGroup
                            value={exitGate}
                            onChange={(e) =>
                                setExitGate(
                                    e.target.value as TransitionExitGate,
                                )
                            }
                        >
                            <FormControlLabel
                                value="none"
                                control={
                                    <Radio
                                        color="primary"
                                        size="small"
                                        disabled={readOnly}
                                    />
                                }
                                label="Move automatically when a condition is met"
                            />
                            <FormControlLabel
                                value="pm_review"
                                control={
                                    <Radio
                                        color="primary"
                                        size="small"
                                        disabled={readOnly}
                                    />
                                }
                                label="PM must approve before moving"
                            />
                        </RadioGroup>

                        {exitGate === 'pm_review' && (
                            <TextField
                                fullWidth
                                size="small"
                                variant="outlined"
                                label="Review prompt"
                                value={reviewPrompt}
                                disabled={readOnly}
                                onChange={(e) =>
                                    setReviewPrompt(e.target.value)
                                }
                                style={{ marginBottom: 16, marginTop: 8 }}
                            />
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    onClick={handleSave}
                    color="primary"
                    variant="contained"
                    disabled={readOnly || enabledCount === 0}
                >
                    Done
                </Button>
            </DialogActions>
        </Dialog>
    )
}
